import plusFill from '@iconify/icons-eva/plus-fill';
import { Icon } from '@iconify/react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  Menu,
  OutlinedInput,
  Stack,
  Tab,
  Tabs
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import { eachDayOfInterval, endOfWeek, format, startOfWeek } from 'date-fns';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { DateRangePicker } from 'react-date-range';
import { capturePHEvents } from '../../../../analytics/posthog.analytics';
import { PH_OFFLINE_ATTENDANCE_CLICK } from '../../../../utils/posthog.eventNames';
import { TableSkeleton } from '../../../../fl-components/table';
import { getClassAttendances } from '../../../../service/attendance.service';
import { BaseOptionChart } from '../../../../fl-components/chart';
import AttendanceModal from '../../../../components/AttendanceModal';
import AttendanceTable from '../../../../components/tables/AttendanceTable';
import EmptyDashboardChart from '../../../../components/EmptyDashboardChart';
import Iconify from '../../../../fl-components/Iconify';

export default function TeacherAttendance({ classDetails }) {
  // Attendance Data
  const [attendanceData, setAttendanceData] = useState(null);
  // Chart loading
  const [chartLoading, setChartLoading] = useState(true);
  // Current tab value
  const [currentTab, setCurrentTab] = useState(0);
  // Selected attendance data
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  // Attendance Modal State
  const [openAttendanceModal, setOpenAttendanceModal] = useState(false);
  // attendanceChartData
  const [attendanceChartData, setAttendanceChartData] = useState([]);
  // Date range
  const [selectionRange, setSelectionRange] = useState({
    startDate: startOfWeek(new Date()),
    endDate: endOfWeek(new Date()),
    key: 'selection'
  });

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const chartOptions = merge(BaseOptionChart(), {
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: eachDayOfInterval({
        start: selectionRange.startDate || new Date(),
        end: selectionRange.endDate || new Date()
      }).map((day) => format(day, 'dd/MM'))
    },
    tooltip: {
      y: {
        formatter: (val) => `Present student ${val}`
      }
    }
  });

  // Chart config
  // const createChartOptions = () => {
  //   const options =
  //   setChartOptions(options);
  // };

  const fetchAttendanceData = () =>
    fetchAttendance(
      moment(selectionRange.startDate).toISOString(),
      moment(selectionRange.endDate).toISOString()
    );

  useEffect(() => {
    // Fetch attendance for class.
    if (attendanceData === null) fetchAttendanceData();
  }, []);

  async function fetchAttendance(start, end) {
    setChartLoading(true);
    const res = await getClassAttendances(classDetails.id, start, end);
    if (res) {
      setAttendanceData(res);
      createChartData(res);
      setChartLoading(false);
      // createChartOptions();
    } else {
      setChartLoading(false);
    }
  }

  // When attendance data is updated
  const createChartData = (attenData) => {
    if (attenData.length) {
      // This is used to keep track same slot id while removing duplicate slotId
      const uniq = {};
      // Get the number of unique slot and make from it for chart data.
      const chartDataArray = attenData
        .filter((ad) => !uniq[ad.slotId] && (uniq[ad.slotId] = true))
        .map((data) => ({ name: data.slot, data: new Array(7).fill(0) }));

      // Get the 7 day date
      eachDayOfInterval({
        start: selectionRange.startDate || new Date(),
        end: selectionRange.endDate || new Date()
      }).forEach((date, index) => {
        // Filter data with created date.
        const filterWithCreatedAt = attenData.filter(
          (ad) => moment(ad.createdAt).format('dd/MM/yyyy') === moment(date).format('dd/MM/yyyy')
        );
        // Loop through that filter data to insert the value in chart data array.
        filterWithCreatedAt.forEach((filterData) => {
          // Find the chart data with slot name and update the count with present student length.
          const chartData = chartDataArray.find((cd) => cd.name === filterData.slot) || null;
          if (chartData?.name) {
            chartData.data[index] =
              chartData.data[index] === 0
                ? filterData.presentStudents.length
                : chartData.data[index];
          } else {
            chartData.data.push(0);
          }
        });
      });

      setAttendanceChartData(chartDataArray);
      return;
    }
    setAttendanceChartData([]);
  };

  return (
    <div style={{ marginTop: 15 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2, md: 4 }}
        alignItems={{ sm: 'center' }}
        justifyContent={{ sm: 'space-between' }}
        mb={1}
      >
        <Tabs
          value={currentTab}
          onChange={(event, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons={false}
          aria-label="scrollable prevent tabs example"
          sx={{ mb: 2 }}
        >
          <Tab label="Graph" />
          <Tab label="List" />
        </Tabs>
        <Button
          variant="contained"
          onClick={() => {
            capturePHEvents(PH_OFFLINE_ATTENDANCE_CLICK, { classId: classDetails?.id });
            setOpenAttendanceModal(true);
          }}
          startIcon={<Icon icon={plusFill} />}
        >
          Offline Attendance
        </Button>
      </Stack>

      <FormControl variant="outlined">
        <InputLabel htmlFor="outlined-adornment-password">Date</InputLabel>
        <OutlinedInput
          id="outlined-adornment-password"
          value={`${moment(selectionRange.startDate).format('DD/MM/yyyy')} - ${moment(
            selectionRange.endDate
          ).format('DD/MM/yyyy')}`}
          onClick={handleClick}
          endAdornment={
            <InputAdornment position="end">
              <Iconify icon="akar-icons:calendar" />
            </InputAdornment>
          }
          label="Date"
        />
      </FormControl>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
      >
        <DateRangePicker
          ranges={[selectionRange]}
          onChange={(e) =>
            setSelectionRange(() => {
              fetchAttendance(
                moment(e.selection.startDate).toISOString(),
                moment(e.selection.endDate).toISOString()
              );
              return {
                startDate: e.selection.startDate,
                endDate: e.selection.endDate,
                key: 'selection'
              };
            })
          }
          maxDate={new Date()}
          focusedRange={[0, 3]}
        />
      </Menu>

      {currentTab === 0 && (
        <>
          {chartLoading && (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%" pt="20px">
              <CircularProgress />
            </Box>
          )}
          {!chartLoading && attendanceData && attendanceData.length === 0 && (
            <EmptyDashboardChart text="Oops! No attendance found" />
          )}
          {/* If api is calling at that time display loading */}
          {attendanceData && attendanceData.length > 0 && (
            <Card sx={{ marginTop: 2, height: chartLoading ? 364 : 'unset' }}>
              <Box
                sx={{
                  mt: chartLoading ? 0 : 3,
                  mx: chartLoading ? 0 : 3,
                  height: chartLoading ? '100%' : 'unset'
                }}
                dir="ltr"
              >
                <ReactApexChart
                  type="bar"
                  series={attendanceChartData}
                  options={chartOptions}
                  height={364}
                />
              </Box>
            </Card>
          )}
        </>
      )}
      {currentTab === 1 && (
        <Box mt={2}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {chartLoading ? (
            <TableSkeleton width="100%" />
          ) : attendanceData.length === 0 ? (
            <EmptyDashboardChart text="Oops! No attendance found" />
          ) : (
            <AttendanceTable
              list={attendanceData}
              onEditHandle={(row) => {
                // setAttendanceOpen(true);
                setOpenAttendanceModal(true);
                setSelectedAttendance(row);
              }}
            />
          )}
        </Box>
      )}

      {/* Attendance Popup */}
      {openAttendanceModal && (
        <AttendanceModal
          isOpen={openAttendanceModal}
          handleClose={() => {
            setOpenAttendanceModal(false);
            setSelectedAttendance(null);
          }}
          classDetails={classDetails}
          editAttendance={selectedAttendance}
          addOrUpdateHandle={() => fetchAttendanceData()}
          key={nanoid()}
        />
      )}
    </div>
  );
}

TeacherAttendance.propTypes = {
  classDetails: PropTypes.shape({
    id: PropTypes.string
  })
};
